<template>
    <div class="co-page">
        <div class="co-header">
            <div class="co-header-left">
                <div class="co-icon-wrap"><i class="fas fa-building"></i></div>
                <h2 class="co-title">Companies</h2>
                <span class="co-count" v-if="companies.length">{{ companies.length }}</span>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CREATE_COMPANIES">
                <button class="co-add-btn" @click="$router.push('/companies/create')">
                    <i class="fas fa-plus"></i> New Company
                </button>
            </AllowPermission>
        </div>

        <div v-if="loading" class="co-loading"><div class="co-spinner"></div><span>Loading...</span></div>
        <div v-else-if="!companies.length" class="co-empty">
            <div class="co-empty-icon"><i class="fas fa-building"></i></div>
            <h3>No companies found</h3>
        </div>
        <div v-else class="co-grid">
            <div class="co-card" v-for="(item, idx) in companies" :key="item.id" :style="{ animationDelay: `${idx * 0.03}s` }">
                <div class="co-card-top">
                    <div class="co-card-icon" :class="coColor(idx)"><i class="fas fa-building"></i></div>
                    <div class="co-card-info">
                        <h4 class="co-card-name">{{ item.name }}</h4>
                        <div class="co-card-loc" v-if="item.city_data || item.state_data || item.country_data">
                            <i class="fas fa-map-marker-alt"></i>
                            {{ [item.city_data?.name, item.state_data?.name, item.country_data?.name].filter(Boolean).join(', ') }}
                        </div>
                    </div>
                </div>
                <div class="co-card-actions">
                    <AllowPermission :permission="PERMISSION_CODES.EDIT_COMPANIES">
                        <a class="co-action-btn co-btn-edit" :href="$router.resolve({ name: 'company-edit', params: { id: item.id } }).href">
                            <i class="fas fa-pen"></i> Edit
                        </a>
                    </AllowPermission>
                    <AllowPermission :permission="PERMISSION_CODES.DELETE_COMPANIES">
                        <button class="co-action-btn co-btn-delete" @click="deleteItem(item.id)"><i class="fas fa-trash-alt"></i> Delete</button>
                    </AllowPermission>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import { PERMISSION_CODES } from "../../store/permission"
import AllowPermission from "../../components/layout/AllowPermission.vue"

export default {
    data() {
        return { loading: true, PERMISSION_CODES, companies: [] }
    },
    components: { AllowPermission },
    async mounted() {
        await this.fetchData()
        this.loading = false
    },
    methods: {
        coColor(i) { return ["co-clr-blue","co-clr-purple","co-clr-teal","co-clr-amber","co-clr-rose"][i % 5] },
        async fetchData() {
            try { const r = await http.get("/companies"); this.companies = r.data.result || [] } catch(e) { console.log(e) }
        },
        async deleteItem(id) {
            if (!confirm("Delete this company?")) return
            try { await http.delete(`/companies/${id}`); this.$toast.success("Deleted"); this.companies = this.companies.filter(c => c.id !== id) }
            catch(e) { this.$toast.error("Error deleting") }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.co-page { animation: coFade 0.3s ease; }
@keyframes coFade { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.co-header { display:flex; align-items:center; justify-content:space-between; gap:12px; background:white; border:1px solid $gray100; border-radius:8px; padding:10px 16px; margin-bottom:12px; box-shadow:$shadowSm; flex-wrap:wrap; }
.co-header-left { display:flex; align-items:center; gap:10px; }
.co-icon-wrap { width:32px; height:32px; border-radius:8px; background:linear-gradient(135deg,$primaryLight,darken($primaryLight,4%)); color:$primaryColor; display:flex; align-items:center; justify-content:center; font-size:14px; }
.co-title { font-size:15px; font-weight:700; color:$gray800; margin:0; }
.co-count { min-width:20px; height:20px; padding:0 6px; border-radius:$radiusFull; font-size:11px; font-weight:700; background:$primaryColor; color:white; display:inline-flex; align-items:center; justify-content:center; }
.co-add-btn { display:inline-flex; align-items:center; gap:6px; padding:7px 14px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; &:hover { background:$primaryDark; } }
.co-loading { display:flex; align-items:center; justify-content:center; gap:10px; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; span { font-size:13px; color:$gray400; } }
.co-spinner { width:24px; height:24px; border:2.5px solid $gray200; border-top-color:$primaryColor; border-radius:50%; animation:coSpin .7s linear infinite; }
@keyframes coSpin { to { transform:rotate(360deg); } }
.co-empty { text-align:center; padding:64px 16px; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowSm; h3 { font-size:14px; font-weight:700; color:$gray600; margin:0; } }
.co-empty-icon { width:48px; height:48px; border-radius:$radiusFull; background:$gray50; border:1px solid $gray100; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; i { font-size:20px; color:$gray300; } }
.co-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:10px; }
.co-card { background:white; border:1px solid $gray100; border-radius:10px; padding:14px; box-shadow:$shadowSm; display:flex; flex-direction:column; gap:10px; animation:coSlide .25s ease both; transition:all $transitionFast; &:hover { border-color:$gray200; box-shadow:$shadowMd; transform:translateY(-1px); } }
@keyframes coSlide { from { opacity:0; transform:translateY(4px); } to { opacity:1; transform:translateY(0); } }
.co-card-top { display:flex; align-items:flex-start; gap:10px; }
.co-card-icon { width:36px; height:36px; border-radius:8px; display:flex; align-items:center; justify-content:center; font-size:14px; flex-shrink:0; }
.co-clr-blue { background:#eff6ff; color:#2563eb; }
.co-clr-purple { background:#f5f3ff; color:#7c3aed; }
.co-clr-teal { background:#f0fdfa; color:#0d9488; }
.co-clr-amber { background:#fffbeb; color:#d97706; }
.co-clr-rose { background:#fff1f2; color:#e11d48; }
.co-card-info { flex:1; min-width:0; }
.co-card-name { font-size:13.5px; font-weight:700; color:$gray800; margin:0 0 3px; line-height:1.3; }
.co-card-loc { font-size:11px; color:$gray400; font-weight:500; display:inline-flex; align-items:center; gap:4px; i { font-size:10px; } }
.co-card-actions { display:flex; gap:6px; margin-top:auto; padding-top:6px; border-top:1px solid $gray100; }
.co-action-btn { display:inline-flex; align-items:center; gap:4px; padding:4px 10px; border:1px solid $gray200; border-radius:5px; font-size:11px; font-weight:600; cursor:pointer; transition:all $transitionFast; font-family:inherit; background:white; text-decoration:none; i { font-size:10px; } }
.co-btn-edit { color:$primaryColor; &:hover { background:$primaryLight; border-color:rgba(0,94,137,0.15); } }
.co-btn-delete { color:$danger; &:hover { background:#fef2f2; border-color:rgba(220,38,38,0.15); } }
@media(max-width:992px) { .co-grid { grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px) { .co-grid { grid-template-columns:1fr; } .co-card { padding:10px; } }
</style>
